import { styled } from '@linaria/react';
import { Flex, Tooltip, type TooltipProps } from '@mantine/core';
import { type CSSProperties, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { newCOLORS } from '../../styles/colors';
import type { TaskRecord } from './tasks-table.type';

/**
 * Renders a task's parent epic as an ellipsis-clamped badge (or an empty-state
 * badge when there is no epic). The tooltip appears only when the text is
 * actually truncated.
 *
 * @param {TaskRecord['epic']} epic - The task's epic, or null/undefined when none
 * @returns {JSX.Element} The epic badge cell
 */
export const EpicCell = ({ epic }: { epic: TaskRecord['epic'] }) => {
  const i18n = useTranslation();
  const badgeStyle = {
    backgroundColor: epic ? newCOLORS.lightBlue2 : newCOLORS.lighterGray2,
    color: epic ? newCOLORS.darkerIndigo : newCOLORS.darkGray2,
  };
  const tooltipProps = { dividerColor: newCOLORS.lighterBlue };

  return (
    <ListDataCell
      data={epic ? [epic] : []}
      style={badgeStyle}
      tooltipProps={tooltipProps}
      defaultText={`No related ${i18n.t('common.parent_task')}`}
    />
  );
};

/**
 * Renders a task's initiatives as a primary ellipsis-clamped badge plus, when
 * there is more than one, a "+N" badge whose tooltip lists the remaining
 * initiatives. The primary badge's tooltip appears only when its text is truncated.
 *
 * @param {TaskRecord['initiatives']} initiatives - The task's initiatives (possibly empty)
 * @returns {JSX.Element} The initiatives badge cell
 */
export const InitiativeCell = ({ initiatives }: { initiatives: TaskRecord['initiatives'] }) => {
  const i18n = useTranslation();
  const badgeStyle = {
    backgroundColor: initiatives.length > 0 ? newCOLORS.lightOrange : newCOLORS.lighterGray2,
    color: initiatives.length > 0 ? newCOLORS.brown : newCOLORS.darkGray2,
  };
  const tooltipProps = {
    dividerColor: newCOLORS.darkYellow,
  };

  return (
    <ListDataCell
      data={initiatives}
      style={badgeStyle}
      tooltipProps={tooltipProps}
      defaultText={`No related ${i18n.t('common.initiative')}`}
    />
  );
};

const ListDataCell = ({
  data,
  style,
  tooltipProps,
  defaultText,
}: {
  data: { id: string; name: string }[];
  style?: CSSProperties;
  tooltipProps?: { dividerColor?: string; backgroundColor?: string };
  defaultText?: string;
}) => {
  const tooltipStyle = {
    ...style,
    backgroundColor: tooltipProps?.backgroundColor || style?.backgroundColor,
    maxWidth: 200,
  };

  if (data.length === 0) {
    return <TruncatingBadge text={defaultText ?? ''} style={style} tooltipStyle={tooltipStyle} />;
  }

  const defaultValue = data[0].name;

  if (data.length === 1) {
    return <TruncatingBadge text={defaultValue} style={style} tooltipStyle={tooltipStyle} />;
  }

  return (
    <Flex direction="row" gap={6}>
      <TruncatingBadge text={defaultValue} style={style} tooltipStyle={tooltipStyle} />
      <CustomTooltip
        label={<ListDataTooltipLabel data={data} dividerColor={tooltipProps?.dividerColor} />}
        style={{ ...tooltipStyle, padding: 0 }}
      >
        <Badge style={{ ...style, cursor: 'pointer' }}>{`+${data.length - 1}`}</Badge>
      </CustomTooltip>
    </Flex>
  );
};

/**
 * Ellipsis-clamped badge that reveals a tooltip only when its text is actually
 * truncated. Truncation is measured lazily on hover (comparing `scrollWidth`/
 * `scrollHeight` against the clamped client size) instead of through a live
 * `ResizeObserver`, so rendering thousands of table rows does not instantiate
 * thousands of observers.
 *
 * @param {string} text - Label rendered in the badge and shown in the tooltip when truncated
 * @param {CSSProperties} [style] - Visual style applied to the badge
 * @param {CSSProperties} [tooltipStyle] - Style applied to the tooltip surface
 * @returns {JSX.Element} The truncation-aware badge
 */
const TruncatingBadge = ({
  text,
  style,
  tooltipStyle,
}: {
  text: string;
  style?: CSSProperties;
  tooltipStyle?: CSSProperties;
}) => {
  const badgeRef = useRef<HTMLDivElement>(null);
  const [opened, setOpened] = useState(false);

  const openIfTruncated = () => {
    const element = badgeRef.current;
    if (element && (element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight)) {
      setOpened(true);
    }
  };

  const closeTooltip = () => setOpened(false);

  return (
    <CustomTooltip label={text} opened={opened} style={tooltipStyle}>
      <EllipsisBadge
        ref={badgeRef}
        tabIndex={0}
        onMouseEnter={openIfTruncated}
        onMouseLeave={closeTooltip}
        onFocus={openIfTruncated}
        onBlur={closeTooltip}
        style={{ ...style, cursor: opened ? 'pointer' : 'default' }}
      >
        {text}
      </EllipsisBadge>
    </CustomTooltip>
  );
};

const CustomTooltip = (props: TooltipProps) => {
  return (
    <Tooltip
      {...props}
      position="bottom-start"
      multiline
      style={{
        padding: '12px',
        borderRadius: 4,
        border: `1px solid ${newCOLORS.lightGray2}`,
        backgroundColor: newCOLORS.white,
        boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.2)',
        color: newCOLORS.darkGray2,
        fontFamily: 'Figtree',
        fontWeight: 400,
        lineHeight: '140%',
        maxWidth: 300,
        ...props.style,
      }}
    />
  );
};

const ListDataTooltipLabel = ({
  data,
  style,
  dividerColor,
}: {
  data: { id: string; name: string }[];
  style?: CSSProperties;
  dividerColor?: string;
}) => {
  return (
    <Flex direction="column" style={style}>
      {data.map((item, index) => (
        <div
          key={item.id}
          style={{
            borderBottom: index !== data.length - 1 ? `1px solid ${dividerColor}` : 'none',
            padding: '12px',
          }}
        >
          {item.name}
        </div>
      ))}
    </Flex>
  );
};

const Badge = styled.div`
  padding: 4px 8px;
  border-radius: 4px;
  font-family: Figtree;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 165%;
  width: fit-content;
`;

const EllipsisBadge = styled(Badge)`
  text-overflow: ellipsis;
  /* Use 'hidden' (not 'clip'): under overflow:clip Blink/Chrome clamps
     scrollWidth/scrollHeight to the box, so the hover truncation check in
     TruncatingBadge (scrollWidth > clientWidth) can never detect overflow.
     'hidden' keeps the element a scroll container so the metrics report the
     real content size in every engine (CSSWG issue #5572). */
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  word-break: break-all;
`;
